import { useEffect, useState } from "react";

import useSimplePopup from "../../../../hooks/useSimplePopup";
import useBLEScanning from "../../../../hooks/useBLEScanning";

import PrimaryButton from "../../../../components/Button/Primary";
import CommonButton from "../../../../components/Button";

import emergencyBluetoothIcon from "../../../../assets/img/emergency/emergency-bluetooth-icon.png";

type Props = { onResult: (result: any) => void; onClose: () => void };

export default function ScanningBluetooth({ onResult, onClose }: Props) {
  const [isScanning, setIsScanning] = useState(false);

  const [simpleComponent, { open }] = useSimplePopup({});
  const [result, { startBLEScanningCall, stopBLEScanningCall }] =
    useBLEScanning({ open });
  const [scanningCount, setScanningCount] = useState(0);
  const [stopSuccess, setStopSuccess] = useState<(() => void) | undefined>(
    undefined
  );

  const DOT_UPDATE_INTERVAL = 500;
  const HALF_SECONDS_IN_MINUTE = 60;

  const getStatusText = () => {
    switch (result.status) {
      case undefined:
        return "수신 요청중";
      case "START_SUCCESS":
        return "수신중";
      case "DATA_RECEIVE":
        return "수신 완료";
      case "START_FAIL":
        return "수신 실패";
      case "STOP_SUCCESS":
        return "수신 종료";
      default:
        return;
    }
  };

  useEffect(() => {
    if (result.isLoading === false) {
      if (result.status === "DATA_RECEIVE") {
        onResult(result.data as string);
        setIsScanning(false);
      } else if (result.status === "START_SUCCESS") {
        setIsScanning(true);
      } else if (result.status === "STOP_SUCCESS") {
        setIsScanning(false);
        stopSuccess?.();
      } else if (result.status === "START_FAIL") {
        setIsScanning(false);
      }
    }
  }, [result.isLoading, result.status, result.data]);

  const calcSeconds = () => {
    return Number(((HALF_SECONDS_IN_MINUTE - scanningCount) / 2).toFixed(0));
  };

  useEffect(() => {
    if (isScanning) {
      const interval = setInterval(() => {
        setScanningCount((prev) => prev + 1);
      }, DOT_UPDATE_INTERVAL);

      return () => clearInterval(interval);
    }
  }, [isScanning]);

  useEffect(() => {
    if (scanningCount >= HALF_SECONDS_IN_MINUTE) {
      setStopSuccess(undefined);
      stopBLEScanningCall();
    }
  }, [scanningCount]);

  useEffect(() => {
    setIsScanning(true);
    startBLEScanningCall();
  }, []);

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col items-center justify-center">
      <div className="flex flex-col justify-center min-h-screen min-w-screen">
        <div className="px-[35px]">
          <img src={emergencyBluetoothIcon} alt="emergency-bluetooth-icon" />
        </div>
        <br />
        <p className="w-full text-center font-black text-xl leading-6">
          관리자와 Bluetooth 연결중
        </p>
        <br />
        <p className="text-base leading-[26px] text-brand-neutral-70 whitespace-pre-line text-center">
          {"Bluetooth가 켜져있는 상태로\n잠시만 대기해주세요."}
        </p>
        <div className="flex items-center justify-center">
          <div className="min-w-[70px]">
            {getStatusText()}
            {isScanning
              ? new Array((scanningCount % 3) + 1).fill(".").join("")
              : ""}
          </div>
          {isScanning && (
            <div className="text-red-500 min-w-[40px]">{`0:${(calcSeconds() > 0
              ? calcSeconds()
              : 0
            )
              .toString()
              .padStart(2, "0")}`}</div>
          )}
        </div>
        <div className="fixed bottom-0 bg-white w-full h-[50px] flex">
          <CommonButton
            variant="secondary"
            className="text-white h-bottom-button w-[100px] flex justify-center items-center text-base font-medium leading-[19px] rounded-none"
            onClick={() => {
              setStopSuccess(onClose);
              stopBLEScanningCall();
            }}
          >
            닫기
          </CommonButton>
          <PrimaryButton
            className="flex-1"
            onClick={() => {
              setIsScanning(true);
              setScanningCount(0);
              startBLEScanningCall();
            }}
            disabled={isScanning}
          >
            재시도
          </PrimaryButton>
        </div>
        {simpleComponent}
      </div>
    </div>
  );
}
